import { useState, useEffect, useCallback, useRef } from "react";
import { WS_URL, MAX_CHART_POINTS } from "../utils/constants";

/**
 * Custom hook for WebSocket connection to the streaming backend.
 * Manages connection state, reconnection, and data updates.
 */
export function useWebSocket() {
  const [isConnected, setIsConnected] = useState(false);
  const [serverState, setServerState] = useState(null);
  const [assets, setAssets] = useState({});
  const [sensorHistory, setSensorHistory] = useState({});
  const [featureHistory, setFeatureHistory] = useState({});
  const [riskHistory, setRiskHistory] = useState({});
  const [recentEvents, setRecentEvents] = useState([]);
  const [stats, setStats] = useState({ eventsReceived: 0, lastUpdate: null });

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const eventCounterRef = useRef(0);  // Running counter for log line numbers

  const sendCommand = useCallback((command) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(command));
    }
  }, []);

  const connect = useCallback(() => {
    // Guard against both OPEN and CONNECTING states to prevent duplicate connections
    // (especially important in React StrictMode which double-renders)
    if (wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    const ws = new WebSocket(WS_URL);

    ws.onopen = () => {
      console.log("WebSocket connected");
      setIsConnected(true);
      // Request initial state
      ws.send(JSON.stringify({ action: "request_state" }));
    };

    ws.onclose = () => {
      console.log("WebSocket disconnected");
      setIsConnected(false);
      // Attempt reconnection after 2 seconds
      reconnectTimeoutRef.current = setTimeout(connect, 2000);
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        handleMessage(message);
      } catch (e) {
        console.error("Error parsing message:", e);
      }
    };

    wsRef.current = ws;
  }, []);

  const handleMessage = useCallback((message) => {
    const { type, data } = message;

    setStats((prev) => ({
      eventsReceived: prev.eventsReceived + 1,
      lastUpdate: new Date().toISOString(),
    }));

    switch (type) {
      case "server_state":
        setServerState(data);
        break;

      case "snapshot":
        // Initial state from server
        setAssets(data.assets || data || {});
        break;

      case "sensor_event":
        // Raw sensor reading
        const sensorAssetId = data.asset_id;
        const sensorTimestamp = data.timestamp;

        setSensorHistory((prev) => {
          const assetHistory = prev[sensorAssetId] || [];
          const newPoint = {
            cycle: sensorTimestamp,
            ...data.sensor_readings,
          };

          // Keep only last MAX_CHART_POINTS
          const updated = [...assetHistory, newPoint].slice(-MAX_CHART_POINTS);
          return { ...prev, [sensorAssetId]: updated };
        });

        // Add raw sensor data to log feed - include all key sensors
        eventCounterRef.current += 1;
        const sensorLineNum = eventCounterRef.current;
        setRecentEvents((prev) => {
          const r = data.sensor_readings || {};
          const newEvent = {
            lineNum: sensorLineNum,
            type: 'sensor',
            asset_id: data.asset_id,
            cycle: sensorTimestamp,
            timestamp: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 }),
            // All useful FD001 sensors with their values
            sensors: {
              T2: r.sensor_2,    // Total temp LPC outlet (°R)
              T3: r.sensor_3,    // Total temp HPC outlet (°R)
              P7: r.sensor_7,    // Total pressure HPC outlet (psia)
              N1: r.sensor_8,    // Physical fan speed (rpm)
              N2: r.sensor_9,    // Physical core speed (rpm)
              P11: r.sensor_11,  // Static pressure HPC outlet (psia)
              FF: r.sensor_12,   // Fuel flow ratio (pps/psi)
              Nc: r.sensor_14,   // Corrected core speed (rpm)
              BPR: r.sensor_15,  // Bypass ratio
              Bl: r.sensor_17,   // Bleed enthalpy
              W21: r.sensor_21,  // LPT coolant bleed (lb/s)
            }
          };
          return [...prev, newEvent].slice(-50);
        });
        break;

      case "feature_event":
        // Windowed feature computation
        const featureAssetId = data.asset_id;

        setFeatureHistory((prev) => {
          const assetHistory = prev[featureAssetId] || [];
          const newPoint = {
            cycle: data.window_end,
            ...extractFeatureValues(data.sensor_features),
          };

          const updated = [...assetHistory, newPoint].slice(-MAX_CHART_POINTS);
          return { ...prev, [featureAssetId]: updated };
        });
        break;

      case "risk_event":
        // AI risk prediction
        const riskAssetId = data.asset_id;

        setRiskHistory((prev) => {
          const assetHistory = prev[riskAssetId] || [];
          const newPoint = {
            cycle: data.window_end,
            risk_score: data.risk_score,
            confidence: data.confidence,
            alert: data.alert,
            top_features: data.top_features || [],  // Include top indicators
            // ML model predictions
            ml_risk_score: data.ml_risk_score,
            ml_predicted_rul: data.ml_predicted_rul,
            used_ml_model: data.used_ml_model,
          };

          const updated = [...assetHistory, newPoint].slice(-MAX_CHART_POINTS);
          return { ...prev, [riskAssetId]: updated };
        });

        // Only add to log if risk is significant (>=25%) or alert triggered
        const isSignificantRisk = data.risk_score >= 0.25 || data.alert;
        if (isSignificantRisk) {
          eventCounterRef.current += 1;
          const riskLineNum = eventCounterRef.current;
          setRecentEvents((prev) => {
            const newEvent = {
              lineNum: riskLineNum,
              asset_id: data.asset_id,
              cycle: data.window_end,
              risk: data.risk_score,
              confidence: data.confidence,
              alert: data.alert,
              top_features: data.top_features || [],
              inference_time: data.inference_time_ms,
              timestamp: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
              mock: data.mock,
              type: 'risk',
            };
            return [...prev, newEvent].slice(-50); // Keep last 50 events
          });
        }
        break;

      case "asset_update":
        // Asset status update
        setAssets((prev) => ({
          ...prev,
          [data.asset_id]: data,
        }));
        break;

      default:
        // Unknown or unused message type (including maintenance messages)
        break;
    }
  }, []);

  // Extract key feature values for charting
  const extractFeatureValues = (sensorFeatures) => {
    const values = {};
    for (const [sensor, features] of Object.entries(sensorFeatures)) {
      const prefix = sensor.replace("sensor_", "S");
      values[`${prefix}_mean`] = features.mean;
      values[`${prefix}_std`] = features.std;
      values[`${prefix}_slope`] = features.slope;
    }
    return values;
  };

  // Reset data when streaming stops
  const resetData = useCallback(() => {
    setAssets({});
    setSensorHistory({});
    setFeatureHistory({});
    setRiskHistory({});
    setRecentEvents([]);
    setStats({ eventsReceived: 0, lastUpdate: null });
    eventCounterRef.current = 0;  // Reset line counter
  }, []);

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  return {
    isConnected,
    serverState,
    assets,
    sensorHistory,
    featureHistory,
    riskHistory,
    recentEvents,
    stats,
    sendCommand,
    resetData,
  };
}
